import React from 'react';
import { mediaQuery, typography } from '../../theme';
import { Title } from '../title';
import styled, { css } from 'styled-components';
import { colors } from '../../theme/color';

type SteppedBaseProps = {
  primary?: boolean;
  secondary?: boolean;
  fullBottomBorder?: boolean;
};

export type SteppedContainerProps = SteppedBaseProps & {
  stepNumber: number;
  title: string;
  noBorder?: boolean;
  preBorder?: boolean;
  className?: string;
  children?: React.ReactNode;
  elementAfterTitle?: React.ReactNode;
};

export const SteppedContainer: React.FC<SteppedContainerProps> = ({
  stepNumber,
  title,
  elementAfterTitle,
  noBorder,
  preBorder = false,
  children,
  primary,
  secondary,
  fullBottomBorder,
  className = '',
  ...props
}) => {
  return (
    <SteppedBaseContainer
      className={`${className} ${primary ? 'primary' : 'secondary'}`}
      primary={primary}
      secondary={secondary}
      fullBottomBorder={fullBottomBorder}
      {...props}
    >
      <StepHeader>
        <PreDotLine shouldDisplay={preBorder} />
        <Step>
          <StepNumber>{stepNumber}</StepNumber>
        </Step>
        <TitleRow>
          <StyledTitle>{title}</StyledTitle>
          {elementAfterTitle && elementAfterTitle}
        </TitleRow>
      </StepHeader>
      <StepSubContainer>
        <DotLine shouldDisplay={!noBorder} />
        <ContentContainer>{children}</ContentContainer>
      </StepSubContainer>
    </SteppedBaseContainer>
  );
};

const StepSubContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

const StepHeader = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  background-color: inherit;
  justify-content: space-between;
`;

const TitleRow = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: row;
  flex: 1;
  justify-content: space-between;
`;

const Dashed = styled.div`
  width: 2px;
  background-image: linear-gradient(to top, black 50%, rgba(255, 255, 255, 0) 0%);
  background-position: bottom;
  background-size: 1px 10px;
  background-repeat: repeat;
  opacity: 0.5;
`;

const DotLine = styled(Dashed)<{ shouldDisplay: boolean }>`
  display: flex;
  padding-bottom: 10px;
  margin: 0 41px -32px 18px;
  flex-shrink: 0;
  ${({ shouldDisplay }) => !shouldDisplay && 'visibility: hidden;'}
`;

const PreDotLine = styled(Dashed)<{ shouldDisplay: boolean }>`
  position: absolute;
  height: 32px;
  padding-bottom: 10px;
  top: -32px;
  height: 14px;
  left: 18px;
  display: ${({ shouldDisplay }) => (shouldDisplay ? 'block' : 'none')};
`;

const Step = styled.div`
  position: absolute;
  left: 1px;
  top: -18px;
  width: 44px;
  height: 64px;
  display: flex;
  align-items: center;
  background-color: inherit;
  z-index: 2;
`;

const StepNumber = styled.div`
  ${typography.headingBrandMediumStyle}
  display: flex;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 3px solid;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  font-size: 23px;
  line-height: 23px;
  flex-shrink: 0;
`;

const StyledTitle = styled(Title)`
  margin-top: 0 !important;
  margin-left: 61px;
  line-height: 100%;
  margin-bottom: 16px;
  text-transform: uppercase !important;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 0;
`;

const SteppedBaseContainer = styled.div<SteppedBaseProps>`
  display: flex;
  flex-direction: column;
  margin-left: -61px;
  color: ${colors.textDefault};
  background-color: ${colors.white};
  padding: 32px calc((100% - 720px) / 2) 32px calc((100% - 720px) / 2);

  &.primary {
    ${DotLine} {
      background-image: linear-gradient(to top, ${colors.textWhite} 50%, rgba(255, 255, 255, 0) 0%);
    }
  }

  @media ${mediaQuery.medium} {
    padding-left: 24px;
    padding-right: 24px;
  }

  @media (max-width: 888px) {
    margin-left: 0;

    ${DotLine}, ${Step} {
      display: none;
    }

    ${StyledTitle} {
      margin-left: 0;
    }

    ${PreDotLine} {
      display: none;
    }
  }

  ${DotLine} {
    ${({ fullBottomBorder }) => {
      if (fullBottomBorder === true) {
        return 'margin-bottom: -32px;';
      } else if (fullBottomBorder === false) {
        return 'margin-bottom: 0;';
      }
    }}
  }

  :last-child {
    ${DotLine} {
      margin-bottom: ${({ fullBottomBorder }) => (fullBottomBorder ? '-32px' : '0')};
    }
  }
`;

type SteppedGroupProps = {
  hideSteps?: boolean;
};

export const SteppedGroup = styled.div<SteppedGroupProps>`
  .primary + .primary,
  .secondary + .secondary {
    padding-top: 0;
  }

  ${({ hideSteps }) =>
    hideSteps &&
    css`
      ${SteppedBaseContainer} {
        margin-left: 0;
        padding: 32px max(calc((100% - 720px) / 2), 20px);

        ${DotLine}, ${Step} {
          display: none;
        }

        ${StyledTitle} {
          margin-left: 0;
        }

        ${PreDotLine} {
          display: none;
        }
      }
    `}
`;
